import React, { useState } from 'react';
import PropTypes, { array } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { shuffle } from 'lodash';
import config from '../../config';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';

import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  IconSpinner,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import facebookImage from '../../assets/allCourseFacebook-1200x630.jpg';

import SectionFAQOnlineTeachingJobs
  from './SectionFAQOnlineTeachingJobs/SectionFAQOnlineTeachingJobs';
import SectionTeachersCarousel from './SectionTeachersCarousel/SectionTeachersCarousel';
import SectionHeroTeachers from './SectionHeroTeachers/SectionHeroTeachers';
import SectionWhyTeach from './SectionWhyTeach/SectionWhyTeach';
import SectionHowItWorks from './SectionHowItWorks/SectionHowItWorks';
import SectionTeachingWithAllCourse
  from './SectionTeachingWithAllCourse/SectionTeachingWithAllCourse';
import SectionAllCourseTeaching from './SectionAllCourseTeaching/SectionAllCourseTeaching';
import SectionBrands from './SectionBrands/SectionBrands';
import SectionTeachersTools from './SectionTeachersTools/SectionTeachersTools';
import SectionFreeCreateProfile from './SectionFreeCreateProfile/SectionFreeCreateProfile';


import css from './OnlineTeachingJobsPage.module.css';
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isTeacherRole, propTypes } from '../../util/types';
import { getSectionsByPage } from '../../util/contentful';
import { types as sdkTypes } from '../../util/sdkLoader';
import classNames from 'classnames';
import SectionJobs from './SectionJobs/SectionJobs';
import SectionFindTeachingJobsByState
  from './SectionFindTeachingJobsByState/SectionFindTeachingJobsByState';
import SectionNotifyMe from './SectionNotifyMe/SectionNotifyMe';
import SectionFindTeachingJobsBySubject
  from './SectionFindTeachingJobsBySubject/SectionFindTeachingJobsBySubject';
import { notifyMe } from './OnlineTeachingJobsPage.duck';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

const { Money } = sdkTypes;

export const OnlineTeachingJobsPageComponent = props => {
  const {
    history,
    intl,
    location,
    scrollingDisabled,
    currentUser,
    teachers,
    onManageDisableScrolling,
    listings,
    searchJobsInProgress,
    jobs,
    searchJobsError,
    stateOptions,
    areasCertifiedOptions,
    searchParams,
    content,
    contentInProgress,
    onNotifyMe,
    notifyMeError,
    notifyMeSuccess,
  } = props;

  const [isOpenModal, setOpenModal] = useState(false);

  const isTeacher = isTeacherRole(currentUser);
  const currentSubject = searchParams?.subject ? areasCertifiedOptions.find(i => i.staticUrl === `${searchParams.subject.replaceAll("-", '_')}`) : null;
  const currentState = searchParams?.state ? stateOptions.find(s => s.short === searchParams.state) : null;

  if (currentSubject === undefined) {
    return <NotFoundPage />;
  }

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const schemaTitle = !currentState && !currentSubject ?
    intl.formatMessage({ id: 'OnlineTeachingJobsPage.schemaTitle' }) :
    !!currentState && !currentSubject ?
      intl.formatMessage(
        { id: 'OnlineTeachingJobsPage.schemaTitleWithState' },
        { state: currentState?.label }
      ) :
      intl.formatMessage(
        { id: 'OnlineTeachingJobsPage.schemaTitleWithStateAndSubject' },
        { subject: currentSubject.parent, state: currentState.label }
      );
  const schemaDescription = !currentState && !currentSubject ?
    intl.formatMessage({ id: 'OnlineTeachingJobsPage.schemaDescription' }) :
    !!currentState && !currentSubject ?
      intl.formatMessage(
        { id: 'OnlineTeachingJobsPage.schemaDescriptionWithState' },
        { state: currentState.label }
      ) :
      intl.formatMessage(
        { id: 'OnlineTeachingJobsPage.schemaDescriptionWithStateAndSubject' },
        { subject: currentSubject.parent, state: currentState.label, stateAbbreviation: currentState.short.toUpperCase() }
      );

  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;
  const filteredTeachers = teachers?.filter(t => {
    const { profile } = t?.attributes;
    const { publicData } = profile;
    const { onboardingProcess } = publicData;
    const isNewUser = onboardingProcess && onboardingProcess?.licenses && onboardingProcess?.licenses.length > 0;
    const isTeacherHasState = onboardingProcess && onboardingProcess?.licenses && onboardingProcess?.licenses[0]?.usState !== null;
    if (isNewUser) {
      return isTeacherHasState;
    } else {
      return t;
    }
  });

  const teachersIds = teachers && teachers.length > 0 ? teachers?.map(t => t?.id?.uuid) : [];
  const listingsWithTeachers = listings && teachersIds && listings.length > 0 && teachersIds.length > 0 ? listings.filter(item => !teachersIds.includes(item.author.id.uuid)) : [];
  const filteredListings = listingsWithTeachers.filter(l => l.attributes.price instanceof Money);

  const randomTeachers = shuffle(filteredTeachers);
  const teachersCustomCard = config?.custom?.teachersCustomCard;
  const contentWhy = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'why')?.fields : null;
  const contentHow = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'how')?.fields : null;
  const contentTeaching = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'teaching')?.fields : null;
  const contentAllCourseTeaching = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'allCourseTeaching')?.fields : null;
  const contentJoin = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'join')?.fields : null;
  const contentNews = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'news')?.fields : null;
  const contentFAQ = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'faq')?.fields : null;

  const sections = content?.sections ? content.sections.map(i => {
    const section = i.fields.sectionName.split('-')[0];

    switch (section) {
      // case 'why':
      //   const contentWhy = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'why')?.fields : null;
      //   return (
      //     <li className={css.section}>
      //       <div className={css.sectionContentFirstChild}>
      //         <SectionWhyTeach
      //           currentState={currentState}
      //           content={contentWhy}
      //         />
      //       </div>
      //     </li>
      //   );
      // case 'how':
      //   const contentHow = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'how')?.fields : null;
      //   return (
      //     <li className={css.section}>
      //       <div className={css.sectionContent}>
      //         <SectionHowItWorks
      //           currentState={currentState}
      //           isTeacher={isTeacher}
      //           currentUser={currentUser}
      //           handleGetStarted={() => setOpenModal(true)}
      //           content={contentHow}
      //         />
      //       </div>
      //     </li>
      //   );
      // case 'teaching':
      //   const contentTeaching = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'teaching')?.fields : null;
      //   return (
      //     <li className={css.section}>
      //       <div className={css.sectionContent}>
      //         <SectionTeachingWithAllCourse
      //           currentState={currentState}
      //           onManageDisableScrolling={onManageDisableScrolling}
      //           currentUser={currentUser}
      //           content={contentTeaching}
      //         />
      //       </div>
      //     </li>
      //   );
      // case 'allCourseTeaching':
      //   const contentAllCourseTeaching = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'allCourseTeaching')?.fields : null;
      //   return (
      //     <li className={css.section}>
      //       <div className={css.sectionAllCourseTeaching}>
      //         <div className={css.sectionContent}>
      //           <SectionAllCourseTeaching
      //             currentState={currentState}
      //             isTeacher={isTeacher}
      //             currentUser={currentUser}
      //             handleGetStarted={() => setOpenModal(true)}
      //             content={contentAllCourseTeaching}
      //           />
      //         </div>
      //       </div>
      //     </li>
      //   );
      // case 'join':
      //   const contentJoin = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'join')?.fields : null;
      //   return (
      //     <li className={css.section}>
      //       <div className={css.sectionContentFirstChild}>
      //         <SectionTeachersCarousel
      //           teachers={randomTeachers?.length > 0 ? randomTeachers : teachersCustomCard}
      //           currentUser={currentUser}
      //           isCustomCard={randomTeachers?.length === 0}
      //           handleGetStarted={() => setOpenModal(true)}
      //           content={contentJoin}
      //         />
      //       </div>
      //     </li>
      //   );
      // case 'news':
      //   const contentNews = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'news')?.fields : null;
      //   return (
      //     <li>
      //       <div className={css.sectionContent}>
      //         <SectionBrands intl={intl} content={contentNews} />
      //       </div>
      //     </li>
      //   );
      // case 'faq':
      //   const contentFAQ = content ? content?.sections.find(e => e.fields.sectionName.split('-')[0] === 'faq')?.fields : null;
      //   return (
      //     <li className={css.sectionContentLastChild}>
      //       <div className={css.sectionContent}>
      //         <SectionFAQOnlineTeachingJobs currentState={currentState} content={contentFAQ} />
      //       </div>
      //     </li>
      //   );
    }
  }) : '';

  const ogImage = content?.metaImage && `https:${content?.metaImage?.fields?.file?.url}`;

  return (
    <Page
      referrer='strict-origin-when-cross-origin'
      currentUser={currentUser}
      pageName='OnlineTeachingJobsPage'
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType='website'
      title={content?.metaTitle || schemaTitle}
      description={content?.metaDescription || schemaDescription}
      facebookImages={ogImage && [{
        image: ogImage,
        url: `${ogImage}`,
        width: 1200,
        height: 630,
      }]}
      twitterImages={ogImage && [
        { url: ogImage, width: 600, height: 314 },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: content?.metaDescription || schemaDescription,
        name: content?.metaTitle || schemaTitle,
        image: ogImage ? [ogImage] : [schemaImage],
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer
            isOpenSigUpModal={isOpenModal}
            setOpenSigUpModal={setOpenModal}
            newSearchDesign
            teacherTopbar
          />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          {!contentInProgress ? (
            <>
              <div className={css.heroContainer}>
                <SectionHeroTeachers
                  rootClassName={css.heroRoot}
                  className={css.hero}
                  currentUser={currentUser}
                  handleGetStarted={() => setOpenModal(true)}
                  currentState={currentState}
                  currentSubject={currentSubject}
                  content={content}
                />
              </div>
              <ul className={css.sections}>
                <li className={css.section}>
                  <div className={css.sectionContentFirstChild}>
                    {currentState && jobs.length === 0 && (!currentUser || (currentUser && isTeacher)) && <SectionNotifyMe
                      currentState={currentState}
                      currentSubject={currentSubject}
                      isTeacher={isTeacher}
                      onSubmit={values => onNotifyMe({
                        state: currentState?.key || null,
                        stateLabel: currentState?.label || null,
                        subject: currentSubject?.key || null ,
                        subjectLabel: currentSubject?.label || null ,
                        ...values,
                      })}
                      notifyMeError={notifyMeError}
                      notifyMeSuccess={notifyMeSuccess}
                      content={content}
                      intl={intl}
                    />}

                    <SectionJobs
                      currentState={currentState}
                      currentSubject={currentSubject}
                      isTeacher={isTeacher}
                      jobs={jobs}
                      content={content}
                    />


                  </div>
                </li>
                <li className={css.section}>
                  <div className={css.sectionContentFirstChild}>
                    {currentState ?
                      <SectionFindTeachingJobsBySubject
                        state={currentState}
                        subject={currentSubject}
                        content={content}
                      /> :
                      <SectionFindTeachingJobsByState state={currentState} content={content} />
                    }
                  </div>
                </li>

                {/*{sections}*/}

                <li className={css.section}>
                  <div className={css.sectionContentFirstChild}>
                    <SectionWhyTeach
                      currentSubject={currentSubject}
                      currentState={currentState}
                      content={contentWhy}
                    />
                  </div>
                </li>
                <li className={css.section}>
                  <div className={css.sectionContent}>
                    <SectionHowItWorks
                      currentState={currentState}
                      isTeacher={isTeacher}
                      currentUser={currentUser}
                      handleGetStarted={() => setOpenModal(true)}
                      content={contentHow}
                    />
                  </div>
                </li>
                <li className={css.section}>
                  <div className={css.sectionContent}>
                    <SectionTeachingWithAllCourse
                      currentSubject={currentSubject}
                      currentState={currentState}
                      onManageDisableScrolling={onManageDisableScrolling}
                      currentUser={currentUser}
                      content={contentTeaching}
                    />
                  </div>
                </li>

                <li className={css.section}>
                  <div className={css.sectionAllCourseTeaching}>
                    <div className={css.sectionContent}>
                      <SectionAllCourseTeaching
                        currentState={currentState}
                        currentSubject={currentSubject}
                        isTeacher={isTeacher}
                        currentUser={currentUser}
                        handleGetStarted={() => setOpenModal(true)}
                        content={contentAllCourseTeaching}
                      />
                    </div>
                  </div>
                </li>
                <li className={css.section}>
                  <div className={css.sectionContentFirstChild}>
                    <SectionTeachersCarousel
                      teachers={randomTeachers?.length > 0 ? randomTeachers : teachersCustomCard}
                      currentUser={currentUser}
                      isCustomCard={randomTeachers?.length === 0}
                      handleGetStarted={() => setOpenModal(true)}
                      content={contentJoin}
                    />
                  </div>
                </li>

                <li className={css.sectionContentLastChild}>
                  <div className={css.sectionContent}>
                    <SectionFAQOnlineTeachingJobs
                      currentState={currentState}
                      currentSubject={currentSubject}
                      content={contentFAQ}
                    />
                  </div>
                </li>
                <li>
                  <div className={css.sectionContent}>
                    <SectionBrands intl={intl} content={contentNews} />
                  </div>
                </li>
                <li className={css.sectionContentLastChild}>
                  <div className={css.sectionContent}>
                    <SectionTeachersTools content={content} />
                  </div>
                </li>
                <li className={classNames(css.section, css.sectionLast)}>
                  <div className={css.sectionFreeCreateProfile}>
                    <div className={css.sectionContent}>
                      <SectionFreeCreateProfile
                        currentState={currentState}
                        isTeacher={isTeacher}
                        currentUser={currentUser}
                        handleGetStarted={() => setOpenModal(true)}
                        content={content}
                      />
                    </div>
                  </div>
                </li>
              </ul>
            </>
          ) : (
            <div className={css.loader}>
              <IconSpinner />
            </div>
          )}
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool, object } = PropTypes;

OnlineTeachingJobsPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  teachers: [],
  reviews: [],
  areasCertifiedOptions: config.custom.areasCertifiedOptions,
  stateOptions: config.custom.stateOptions,
};

OnlineTeachingJobsPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  listings: array,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,

  teachers: array,

  fetchTransactionsInProgress: bool.isRequired,
  fetchTransactionsError: propTypes.error,
  reviews: array,

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    searchTeachersInProgress,
    searchTeachersError,
    teachers,
    currentPageResultIds,
    searchInProgress,
    searchListingsError,
    fetchTransactionsInProgress,
    fetchTransactionsError,
    reviews,
    transactionRefs,
    searchJobsInProgress,
    searchMapJobsIds,
    searchJobsError,
    searchParams,
    content,
    contentInProgress,
    notifyMeError,
    notifyMeInProgress,
    notifyMeSuccess,
  } = state.OnlineTeachingJobsPage;

  const pageListings = getListingsById(state, currentPageResultIds);
  const jobs = getListingsById(state, searchMapJobsIds);

  return {
    currentUser,
    searchTeachersInProgress,
    searchTeachersError,
    teachers,
    scrollingDisabled: isScrollingDisabled(state),
    listings: pageListings,
    searchInProgress,
    searchListingsError,
    fetchTransactionsInProgress,
    fetchTransactionsError,
    reviews,
    transactions: getMarketplaceEntities(state, transactionRefs),
    searchJobsInProgress,
    jobs,
    searchJobsError,
    searchParams,
    content,
    contentInProgress,
    notifyMeError,
    notifyMeInProgress,
    notifyMeSuccess,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) => dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onNotifyMe: params => dispatch(notifyMe(params)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const OnlineTeachingJobsPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
)(OnlineTeachingJobsPageComponent);

export default OnlineTeachingJobsPage;
